import { useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import type { MarketingConsentStrings } from '@/i18n/marketingConsent';
import type {
  MarketingConsentNotice,
  MarketingConsentProjection,
  MarketingConsentStatus,
} from '@/lib/api/types';

export interface MarketingConsentSectionProps {
  strings: MarketingConsentStrings;
  notice: MarketingConsentNotice | null;
  consent: MarketingConsentProjection | null;
  isLoading: boolean;
  loadError: boolean;
  isUpdating: boolean;
  mutationError: boolean;
  isNoticeStale: boolean;
  lastSuccess: MarketingConsentStatus | null;
  onRetryLoad: () => void;
  onUpdate: (status: MarketingConsentStatus) => void;
}

const sectionTitleId = 'marketing-consent-title';

function statusText(
  consent: MarketingConsentProjection,
  notice: MarketingConsentNotice | null,
  strings: MarketingConsentStrings
): string {
  if (
    notice === null ||
    (!consent.can_grant && !consent.can_withdraw)
  ) {
    return strings.statusOff;
  }
  if (consent.effective_status === 'granted') {
    return strings.statusGranted;
  }
  if (
    consent.recorded_status === 'granted' &&
    consent.consent_version === notice.version
  ) {
    return strings.statusContactBlocked;
  }
  if (consent.recorded_status === null) {
    return strings.statusNotChosen;
  }
  if (consent.recorded_status === 'granted') {
    return strings.statusExpired;
  }
  return strings.statusWithdrawn;
}

export function MarketingConsentSection({
  strings,
  notice,
  consent,
  isLoading,
  loadError,
  isUpdating,
  mutationError,
  isNoticeStale,
  lastSuccess,
  onRetryLoad,
  onUpdate,
}: MarketingConsentSectionProps) {
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const withdrawTriggerRef = useRef<HTMLElement | null>(null);
  const statusRef = useRef<HTMLParagraphElement | null>(null);
  const canAct = notice !== null && consent !== null;
  const isContactBlocked =
    notice !== null &&
    consent?.recorded_status === 'granted' &&
    consent.effective_status === 'withdrawn' &&
    consent.consent_version === notice.version;
  const hasPreviousGrant =
    consent?.recorded_status === 'granted' &&
    consent.effective_status === 'withdrawn' &&
    !isContactBlocked;
  const isNoEvidence = consent?.recorded_status === null;
  const isUnavailable =
    consent !== null &&
    (notice === null || (!consent.can_grant && !consent.can_withdraw));

  return (
    <section
      aria-labelledby={sectionTitleId}
      className="mt-6"
      role="region"
    >
      <h2 id={sectionTitleId} className="text-lg font-semibold">
        {strings.sectionTitle}
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">
        {strings.sectionDescription}
      </p>

      {isLoading ? (
        <div className="mt-4 space-y-3" aria-live="polite">
          <p>{strings.loading}</p>
          <Skeleton aria-hidden="true" className="h-36 w-full" />
        </div>
      ) : loadError ? (
        <div className="mt-4 rounded-lg border bg-background p-5 text-center">
          <p className="text-sm text-destructive" role="alert">
            {strings.loadError}
          </p>
          <Button
            className="mt-4"
            type="button"
            variant="outline"
            onClick={onRetryLoad}
          >
            {strings.retry}
          </Button>
        </div>
      ) : consent !== null ? (
        <div className="mt-4 rounded-lg border bg-background p-5">
          {notice !== null ? (
            <div>
              <a
                aria-label={`${strings.sectionTitle}: ${strings.noticeLink}`}
                className="font-medium text-primary underline underline-offset-4"
                href={notice.url}
              >
                {strings.noticeLink}
              </a>
              <p className="mt-2 text-sm text-muted-foreground">
                {strings.noticeVersion}:{' '}
                <bdi dir="ltr">{notice.version}</bdi>
              </p>
            </div>
          ) : null}

          <p
            ref={statusRef}
            className="mt-4 text-sm font-medium"
            role="status"
            tabIndex={-1}
          >
            {isUpdating
              ? strings.saving
              : isContactBlocked
                ? strings.statusContactBlocked
                : lastSuccess === 'granted'
                  ? strings.grantSuccess
                  : lastSuccess === 'withdrawn'
                    ? strings.withdrawSuccess
                    : statusText(consent, notice, strings)}
          </p>

          {isNoticeStale ? (
            <p className="mt-3 text-sm text-destructive" role="alert">
              {strings.staleNotice}
            </p>
          ) : null}

          {mutationError ? (
            <p className="mt-3 text-sm text-destructive" role="alert">
              {strings.mutationError}
            </p>
          ) : null}

          {isUnavailable ? (
            <p className="mt-3 text-sm text-muted-foreground">
              {strings.statusUnavailable}
            </p>
          ) : null}

          {canAct ? (
            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              {consent.can_grant ? (
                <Button
                  disabled={isUpdating}
                  type="button"
                  onClick={() => onUpdate('granted')}
                >
                  {isContactBlocked
                    ? strings.resolveContactBlock
                    : hasPreviousGrant
                      ? strings.renew
                      : strings.grant}
                </Button>
              ) : null}

              {consent.can_withdraw ? (
                <Button
                  disabled={isUpdating}
                  type="button"
                  variant="outline"
                  onClick={(event) => {
                    if (isNoEvidence) {
                      onUpdate('withdrawn');
                    } else {
                      withdrawTriggerRef.current = event.currentTarget;
                      setWithdrawDialogOpen(true);
                    }
                  }}
                >
                  {isNoEvidence
                    ? strings.decline
                    : hasPreviousGrant
                      ? strings.withdrawPrevious
                      : strings.withdraw}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      <AlertDialog
        open={withdrawDialogOpen}
        onOpenChange={(open) => {
          if (!isUpdating) {
            setWithdrawDialogOpen(open);
          }
        }}
      >
        <AlertDialogContent
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            queueMicrotask(() => {
              const canRestoreToTrigger =
                withdrawTriggerRef.current?.isConnected &&
                !withdrawTriggerRef.current.matches(
                  ':disabled, [aria-disabled="true"]'
                );
              const focusTarget = canRestoreToTrigger
                ? withdrawTriggerRef.current
                : statusRef.current?.isConnected
                  ? statusRef.current
                  : null;

              focusTarget?.focus();
            });
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>{strings.withdrawConfirmTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {strings.withdrawConfirmBody}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUpdating}>
              {strings.withdrawConfirmCancel}
            </AlertDialogCancel>
            <AlertDialogAction
              aria-busy={isUpdating || undefined}
              disabled={isUpdating}
              onClick={() => onUpdate('withdrawn')}
            >
              {strings.withdrawConfirmAction}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
